/**
 * Google Tag Manager DataLayer Functions
 *
 * SSR-safe implementation that uses a helper function pattern
 * to avoid duplicate Window interface declarations.
 */

import {
  type AddPaymentInfoParams,
  type AnalysisCompleteParams,
  type AnalysisStartParams,
  type BeginCheckoutParams,
  type ChatMessageSendParams,
  type ChatShareParams,
  type ChatStartParams,
  type CtaClickParams,
  EVENTS,
  type FormStartParams,
  type FormSubmitParams,
  type GenerateLeadParams,
  type LoginParams,
  type NewsletterSignupParams,
  type PurchaseParams,
  type ShareParams,
  type SignUpParams,
  type UpgradeViewParams,
} from "./events";

// =============================================================================
// DataLayer Access (SSR-Safe)
// =============================================================================

type WindowWithDataLayer = {
  dataLayer?: Record<string, unknown>[];
};

/**
 * Get the dataLayer array, creating it if necessary.
 * Returns empty array during SSR (no-op).
 */
function getDataLayer(): Record<string, unknown>[] {
  if (typeof window === "undefined") {
    return [];
  }

  const win = window as unknown as WindowWithDataLayer;
  if (!win.dataLayer) {
    win.dataLayer = [];
  }
  return win.dataLayer;
}

/**
 * Check if GTM is configured
 */
export function isGtmConfigured(): boolean {
  return Boolean(process.env.NEXT_PUBLIC_GTM_ID);
}

// =============================================================================
// Core Tracking Function
// =============================================================================

/**
 * Push an event to the dataLayer.
 * Silent no-op if GTM is not configured or during SSR.
 */
export function trackEvent(
  eventName: string,
  params: Record<string, unknown> = {}
): void {
  // Skip if GTM not configured
  if (!isGtmConfigured()) {
    return;
  }

  const dataLayer = getDataLayer();

  // If we got an empty array from SSR, do nothing
  if (dataLayer.length === 0 && typeof window === "undefined") {
    return;
  }

  dataLayer.push({
    event: eventName,
    ...params,
  });
}

/**
 * Push event data without an event name (for setting variables)
 */
export function pushToDataLayer(data: Record<string, unknown>): void {
  if (!isGtmConfigured()) {
    return;
  }

  const dataLayer = getDataLayer();
  dataLayer.push(data);
}

// =============================================================================
// Typed Event Tracking Functions
// =============================================================================

/**
 * Track CTA (Call-to-Action) clicks
 */
export function trackCtaClick(params: CtaClickParams): void {
  trackEvent(EVENTS.CTA_CLICK, params);
}

/**
 * Track form interaction start (fire once per form instance)
 */
export function trackFormStart(params: FormStartParams): void {
  trackEvent(EVENTS.FORM_START, params);
}

/**
 * Track form submission
 */
export function trackFormSubmit(params: FormSubmitParams): void {
  trackEvent(EVENTS.FORM_SUBMIT, params);
}

/**
 * Track lead generation (email captured, demo requested)
 */
export function trackGenerateLead(params: GenerateLeadParams): void {
  trackEvent(EVENTS.GENERATE_LEAD, params);
}

/**
 * Track newsletter signup
 */
export function trackNewsletterSignup(params: NewsletterSignupParams): void {
  trackEvent(EVENTS.NEWSLETTER_SIGNUP, params);
}

/**
 * Track checkout flow initiation
 */
export function trackBeginCheckout(params: BeginCheckoutParams): void {
  trackEvent(EVENTS.BEGIN_CHECKOUT, params);
}

/**
 * Track payment info entry
 */
export function trackAddPaymentInfo(params: AddPaymentInfoParams): void {
  trackEvent(EVENTS.ADD_PAYMENT_INFO, params);
}

/**
 * Track purchase completion
 */
export function trackPurchase(params: PurchaseParams): void {
  trackEvent(EVENTS.PURCHASE, params);
}

/**
 * Track upgrade prompt view
 */
export function trackUpgradeView(params: UpgradeViewParams): void {
  trackEvent(EVENTS.UPGRADE_VIEW, params);
}

/**
 * Track analysis start (core product action)
 */
export function trackAnalysisStart(params: AnalysisStartParams): void {
  trackEvent(EVENTS.ANALYSIS_START, params);
}

/**
 * Track analysis completion (core product action)
 */
export function trackAnalysisComplete(params: AnalysisCompleteParams): void {
  trackEvent(EVENTS.ANALYSIS_COMPLETE, params);
}

/**
 * Track share actions
 */
export function trackShare(params: ShareParams): void {
  trackEvent(EVENTS.SHARE, params);
}

/**
 * Track chat session start
 */
export function trackChatStart(params: ChatStartParams): void {
  trackEvent(EVENTS.CHAT_START, params);
}

/**
 * Track chat message send
 */
export function trackChatMessageSend(params: ChatMessageSendParams): void {
  trackEvent(EVENTS.CHAT_MESSAGE_SEND, params);
}

/**
 * Track chat share
 */
export function trackChatShare(params: ChatShareParams): void {
  trackEvent(EVENTS.CHAT_SHARE, params);
}

/**
 * Track user sign up
 */
export function trackSignUp(params: SignUpParams): void {
  trackEvent(EVENTS.SIGN_UP, params);
}

/**
 * Track user login
 */
export function trackLogin(params: LoginParams): void {
  trackEvent(EVENTS.LOGIN, params);
}

// =============================================================================
// User Properties
// =============================================================================

/**
 * Set user properties in the dataLayer
 */
export function setUserProperties(properties: {
  user_id?: string;
  user_tier?: string;
  is_guest?: boolean;
}): void {
  pushToDataLayer({
    user_id: properties.user_id,
    user_tier: properties.user_tier,
    is_guest: properties.is_guest,
  });
}

/**
 * Clear user properties (on logout)
 */
export function clearUserProperties(): void {
  pushToDataLayer({
    user_id: undefined,
    user_tier: undefined,
    is_guest: undefined,
  });
}
